import { LAYOUT_GAMMA75_KEYCODES } from "../components/Keyboard/keyboardLayouts";

/** Fn 层未映射的物理键：穿透键 △（keyCode 1） */
export const GAMMA75_FN_TRIANGLE = 1;

/** matrix (row,col) → Fn 层 consumer / 控制键码 */
const GAMMA75_FN_CELL_OVERRIDES: Readonly<Record<string, number>> = {
  // Row 0 — F 区
  "0,1": 62210, // 灯光减
  "0,2": 62211, // 灯光+
  "0,3": 8595, // Win 应用控制切换（0x2193）
  "0,4": 8594, // 计算器
  "0,5": 8596, // 我的电脑
  "0,6": 0, // 空
  "0,7": 8374, // 上一首
  "0,8": 8397, // 播放/暂停
  "0,9": 8373, // 下一首
  "0,10": 8418, // 静音
  "0,11": 8426, // 音量-
  "0,12": 8425, // 音量+
  "0,14": 77, // End
  "0,15": 72, // Pause
  // Row 1 — 数字行
  "1,1": 61957, // 板载1
  "1,2": 61958, // 板载2
  "1,3": 61959, // 板载3
  "1,4": 61960, // 板载4
  "1,13": 62215, // 灯光开关
  // Row 2 — QWERTY 区 `\|`
  "2,11": 62224, // 灯带灯效模式切换
  "2,12": 62225, // 灯带颜色切换
  "2,13": 62208, // 灯效模式切换
  // Row 3 — Caps 行
  "3,0": 61952, // Caps：恢复出厂设置（长按）
  "3,1": 61953, // A：切换至 Windows 布局（长按）
  "3,2": 61954, // S：切换至 Mac 布局（长按）
  "3,12": 62209, // 灯光颜色（单色切换）
  // Row 4 — Shift 行（LShift 后 col1 占位列，/? 在 col13）
  "4,13": 62211, // 灯光亮度+
  // Row 5 — 底行（5-1 未映射 → △）
  "5,13": 62213, // ← 灯光速度-
  "5,14": 62210, // ↓ 灯光亮度-
  "5,15": 62212, // → 灯光速度+
};

/**
 * 生成 Gamma75 Fn 层展示矩阵：物理位置与 Base 相同，仅覆盖指定 (row,col) 的功能码，其余为 △。
 */
export function buildGamma75FnLayerKeyCodes(): number[][] {
  return LAYOUT_GAMMA75_KEYCODES.map((row, rowIndex) =>
    row.map((baseCell, col) => {
      const overrideKey = `${rowIndex},${col}`;
      if (
        Object.prototype.hasOwnProperty.call(
          GAMMA75_FN_CELL_OVERRIDES,
          overrideKey
        )
      ) {
        return GAMMA75_FN_CELL_OVERRIDES[overrideKey];
      }
      if (baseCell === 0) return 0;
      return GAMMA75_FN_TRIANGLE;
    })
  );
}

/** SDK 上报为实键（非空、非穿透）时优先用 SDK，否则回退静态出厂 Fn 映射 */
function resolveGamma75FnCell(
  sdkCell: number | undefined,
  staticCell: number
): number {
  if (sdkCell !== undefined && sdkCell !== 0 && sdkCell !== 1) {
    return sdkCell;
  }
  return staticCell;
}

export function applyGamma75FnLayerToSdkRows(
  rows: { layer: number; row: number; keyboardLayout: number[] }[]
): { layer: number; row: number; keyboardLayout: number[] }[] {
  const fnMatrix = buildGamma75FnLayerKeyCodes();
  return rows.map((row) => {
    const staticRow = fnMatrix[row.row];
    if (!staticRow) return row;
    const sdkLayout = row.keyboardLayout ?? [];
    const len = Math.max(staticRow.length, sdkLayout.length);
    const merged: number[] = [];
    for (let col = 0; col < len; col++) {
      const staticCell = staticRow[col] ?? GAMMA75_FN_TRIANGLE;
      merged[col] = resolveGamma75FnCell(sdkLayout[col], staticCell);
    }
    return { ...row, keyboardLayout: merged };
  });
}

/** Fn 层指定为「空键」(0) 且 Base 矩阵该格有实键 → 须保留键位，不能压紧掉或渲染成 △ / 间隙 */
export function isGamma75FnEmptyPhysicalCell(
  rowIndex: number,
  matrixCol: number,
  keyCode: number
): boolean {
  if (keyCode !== 0) return false;
  const ref = LAYOUT_GAMMA75_KEYCODES[rowIndex]?.[matrixCol];
  return typeof ref === "number" && ref !== 0;
}

/**
 * ##FIX:fn层布局偏移
 * gamma75设备键盘布局转换处理
 * 规则：
 * 1. 遇到 KEY_0 且开启 multipleWidth 标记为偏移起始，当前键改为 __gap__，宽度置0
 * 2. isOffset开启后，后续按键继承前一个按键的 multipleWidth
 * @param keyConfigLayout 原始键盘二维布局数组
 * @returns 处理完成后的布局数组
 */
export function formatGamma75KeyLayout<T extends Record<string, any>[][]>(
  keyConfigLayout: T
): T {
  return keyConfigLayout.map((row) => {
    let isOffset = false;
    return row.map((keyItem, index) => {
      // 每行开始重置偏移标记
      if (index === 0) {
        isOffset = false;
      }

      // 命中 KEY_0 宽键，转为占位gap
      if (keyItem.code === "KEY_0" && keyItem.multipleWidth) {
        isOffset = true;
        return {
          ...keyItem,
          code: "__gap__",
          multipleWidth: 0,
        };
      }

      // 偏移模式：继承上一个按键宽度
      if (isOffset) {
        const prevKey = row[index - 1];
        return {
          ...keyItem,
          multipleWidth: prevKey.multipleWidth ?? keyItem.multipleWidth,
        };
      }

      return keyItem;
    });
  }) as T;
}
